"use client";
import React, { useState, useEffect } from "react";
import { Check, Palette } from "lucide-react";
import { HexColorInput, HexColorPicker } from "react-colorful";
import { useTheme } from "@/contexts/ThemeContext";

interface ColorPickerProps {
  color: string;
  onChange: (color: string) => void;
  label?: string;
}

const DEFAULT_PRESET_COLORS = [
  "#5659FF",
  "#FF5659",
  "#56FF59",
  "#FF9F43",
  "#7367F0",
  "#EA5455",
  "#28C76F",
  "#00CFE8",
  "#FF6B35",
  "#6C5CE7",
  "#A8AAAE",
  "#1F2233",
  "#FFFFFF",
  "#000000",
  "#F8F9FA",
];

export function ColorPicker({ color, onChange, label }: ColorPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { themeColors, isThemeLoading } = useTheme();
  const [customValue, setCustomValue] = useState<string>(color || "#000000");
  const containerRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (typeof color === "string" && color) {
      setCustomValue(color);
    }
  }, [color]);

  const handlePresetClick = (presetColor: string) => {
    onChange(presetColor);
    setIsOpen(false);
  };

  const handleThemeColorClick = (identifier: string) => {
    onChange(identifier); // Pass the identifier (e.g., "primary", "secondary")
    setIsOpen(false);
  };

  const handleButtonClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const handleDropdownClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  // Get theme colors as an array for display
  const getThemeColorsArray = (): {
    label: string;
    color: string;
    identifier: string;
  }[] => {
    if (!themeColors) return [];

    return [
      { label: "Primary", color: themeColors.primary, identifier: "primary" },
      {
        label: "Secondary",
        color: themeColors.secondary,
        identifier: "secondary",
      },
      { label: "Accent", color: themeColors.accent, identifier: "accent" },
      { label: "Text", color: themeColors.text, identifier: "text" },
      {
        label: "Primary Gradient Start",
        color: themeColors.primaryGradientStart,
        identifier: "primaryGradientStart",
      },
      {
        label: "Primary Gradient End",
        color: themeColors.primaryGradientEnd,
        identifier: "primaryGradientEnd",
      },
      {
        label: "Secondary Gradient Start",
        color: themeColors.secondaryGradientStart,
        identifier: "secondaryGradientStart",
      },
      {
        label: "Secondary Gradient End",
        color: themeColors.secondaryGradientEnd,
        identifier: "secondaryGradientEnd",
      },
    ].filter((item) => item.color && item.color !== ""); // Filter out empty colors
  };

  const getDisplayColor = () => {
    // If color is a theme identifier, try to get the actual color from theme
    if (themeColors && typeof color === "string") {
      const themeColorMap: Record<string, string> = {
        primary: themeColors.primary,
        secondary: themeColors.secondary,
        accent: themeColors.accent,
        text: themeColors.text,
        primaryGradientStart: themeColors.primaryGradientStart,
        primaryGradientEnd: themeColors.primaryGradientEnd,
        secondaryGradientStart: themeColors.secondaryGradientStart,
        secondaryGradientEnd: themeColors.secondaryGradientEnd,
      };

      return themeColorMap[color] || color;
    }
    return color;
  };

  const getDisplayLabel = () => {
    // If color is a theme identifier, show a more user-friendly label
    const themeColorLabels: Record<string, string> = {
      primary: "Primary",
      secondary: "Secondary",
      accent: "Accent",
      text: "Text",
      primaryGradientStart: "Primary Gradient Start",
      primaryGradientEnd: "Primary Gradient End",
      secondaryGradientStart: "Secondary Gradient Start",
      secondaryGradientEnd: "Secondary Gradient End",
    };

    return themeColorLabels[color] || color.toUpperCase();
  };

  return (
    <div className="relative" ref={containerRef}>
      {label && (
        <label className="text-sm font-medium block mb-2">{label}</label>
      )}

      {/* Color Display Button */}
      <button
        onClick={handleButtonClick}
        className="w-full h-10 rounded-lg border border-border dark:border-auth-background flex items-center space-x-3 px-3 hover:bg-muted dark:hover:bg-auth-background transition-colors"
      >
        <div
          className="w-6 h-6 rounded border border-border dark:border-auth-background shadow-sm"
          style={{ backgroundColor: getDisplayColor() }}
        />
        <span className="text-sm font-mono flex-1 text-left">
          {getDisplayLabel()}
        </span>
      </button>

      {/* Color Picker Dropdown */}
      {isOpen && (
        <div
          className="absolute top-full left-0 right-0 mt-1 bg-card dark:bg-auth-card border border-border dark:border-auth-background rounded-lg shadow-theme-lg overflow-y-auto"
          onClick={handleDropdownClick}
        >
          <div className="p-3 space-y-4">
            {/* Theme Colors */}
            {themeColors && getThemeColorsArray().length > 0 && (
              <div>
                <h4 className="text-xs font-medium text-muted-foreground mb-2 flex items-center space-x-1">
                  <Palette className="w-3 h-3" />
                  <span>Site Theme Colors</span>
                </h4>
                <div className="space-y-2">
                  {getThemeColorsArray().map((themeColor, index) => (
                    <button
                      key={`theme-${index}`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleThemeColorClick(themeColor.identifier);
                      }}
                      className="w-full flex items-center space-x-3 p-2 rounded hover:bg-muted dark:hover:bg-auth-background transition-colors group"
                    >
                      <div
                        className="w-6 h-6 rounded border border-border dark:border-auth-background flex items-center justify-center relative"
                        style={{ backgroundColor: themeColor.color }}
                      >
                        {color === themeColor.identifier && (
                          <Check className="w-3 h-3 text-white drop-shadow-lg" />
                        )}
                      </div>
                      <span className="text-xs text-left flex-1 group-hover:text-foreground">
                        {themeColor.label}
                      </span>
                      <span className="text-xs font-mono text-muted-foreground">
                        {themeColor.color.toUpperCase()}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Loading State for Theme Colors */}
            {isThemeLoading && (
              <div className="animate-pulse space-y-2">
                <h4 className="text-xs font-medium text-muted-foreground mb-2 flex items-center space-x-1">
                  <Palette className="w-3 h-3" />
                  <span>Loading Theme Colors...</span>
                </h4>
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="flex items-center space-x-3 p-2">
                    <div className="w-6 h-6 bg-muted dark:bg-auth-background rounded"></div>
                    <div className="flex-1 h-3 bg-muted dark:bg-auth-background rounded"></div>
                  </div>
                ))}
              </div>
            )}

            {/* Preset Colors */}
            <div>
              <h4 className="text-xs font-medium text-muted-foreground mb-2">
                Preset Colors
              </h4>
              <div className="grid grid-cols-5 gap-2">
                {DEFAULT_PRESET_COLORS.map((presetColor: string) => (
                  <button
                    key={presetColor}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handlePresetClick(presetColor);
                    }}
                    className="w-8 h-8 rounded border border-border dark:border-auth-background hover:scale-110 transition-transform relative"
                    style={{ backgroundColor: presetColor }}
                  >
                    {color === presetColor && (
                      <Check className="w-4 h-4 text-white absolute inset-0 m-auto drop-shadow-lg" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Custom Color Input */}
            <div>
              <h4 className="text-xs font-medium text-muted-foreground mb-2">
                Custom Color
              </h4>
              <div className="space-y-3">
                <div className="rounded-lg border border-border dark:border-auth-background p-3 bg-card dark:bg-auth-card">
                  <HexColorPicker
                    color={customValue || "#000000"}
                    onChange={(nextColor) => {
                      setCustomValue(nextColor);
                      onChange(nextColor);
                    }}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <div
                    className="h-8 w-8 rounded border border-border dark:border-auth-background shadow-sm"
                    style={{ backgroundColor: customValue || "#000000" }}
                  />
                  <HexColorInput
                    color={customValue || "#000000"}
                    onChange={(nextColor) => {
                      setCustomValue(nextColor);
                      onChange(nextColor);
                    }}
                    prefixed
                    className="flex-1 px-2 py-1 text-xs font-mono bg-muted dark:bg-auth-background border border-border dark:border-auth-background rounded focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="#000000"
                  />
                </div>
                <div className="text-[11px] text-muted-foreground">
                  Pick a color or paste a hex value.
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
